import { Injectable } from '@angular/core';
import fuzzysort from 'fuzzysort';
import { SitemapEntry, SitemapSearchCriteria } from './sitemap.types';

interface SearchableSitemapEntry {
	readonly entry: SitemapEntry;
	readonly titleTokens: readonly string[];
	readonly guidanceTokens: readonly string[];
	readonly searchTermTokens: readonly (readonly string[])[];
	readonly tokenWeights: ReadonlyMap<string, number>;
}

interface RankedSitemapEntry {
	readonly entry: SitemapEntry;
	readonly score: number;
}

const QUESTION_FILLER_WORDS = new Set([
	'a',
	'an',
	'can',
	'could',
	'do',
	'does',
	'for',
	'how',
	'i',
	'in',
	'is',
	'me',
	'my',
	'of',
	'on',
	'please',
	'the',
	'tell',
	'to',
	'want',
	'what',
	'where',
	'which',
	'who',
	'with',
	'would',
	'you',
]);

const TITLE_WEIGHT = 3;
const SEARCH_TERM_WEIGHT = 2;
const GUIDANCE_WEIGHT = 1;

function singularize(token: string): string {
	if (token.length > 4 && token.endsWith('ies')) {
		return `${token.slice(0, -3)}y`;
	}

	if (
		token.length > 3 &&
		token.endsWith('s') &&
		!token.endsWith('ss') &&
		!token.endsWith('us')
	) {
		return token.slice(0, -1);
	}

	return token;
}

function normalizeTokens(value: string, removeQuestionFiller = true): string[] {
	return (
		value
			.normalize('NFKD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[’']/g, '')
			.toLowerCase()
			.match(/[a-z0-9]+/g) ?? []
	)
		.filter(
			(token) => !removeQuestionFiller || !QUESTION_FILLER_WORDS.has(token),
		)
		.map(singularize);
}

function addTokens(
	tokenWeights: Map<string, number>,
	tokens: readonly string[],
	weight: number,
): void {
	for (const token of tokens) {
		tokenWeights.set(token, Math.max(tokenWeights.get(token) ?? 0, weight));
	}
}

function buildSearchableEntry(entry: SitemapEntry): SearchableSitemapEntry {
	const titleTokens = normalizeTokens(entry.title);
	const guidanceTokens = normalizeTokens(entry.guidance);
	const searchTermTokens = entry.searchTerms.map((searchTerm) =>
		normalizeTokens(searchTerm),
	);
	const tokenWeights = new Map<string, number>();

	addTokens(tokenWeights, titleTokens, TITLE_WEIGHT);
	addTokens(tokenWeights, guidanceTokens, GUIDANCE_WEIGHT);
	searchTermTokens.forEach((tokens) =>
		addTokens(tokenWeights, tokens, SEARCH_TERM_WEIGHT),
	);

	return {
		entry,
		titleTokens,
		guidanceTokens,
		searchTermTokens,
		tokenWeights,
	};
}

function sequencesEqual(
	first: readonly string[],
	second: readonly string[],
): boolean {
	return (
		first.length === second.length &&
		first.every((token, index) => token === second[index])
	);
}

function containsSequence(
	tokens: readonly string[],
	sequence: readonly string[],
): boolean {
	return tokens.some((_, startIndex) =>
		sequence.every(
			(token, sequenceIndex) => tokens[startIndex + sequenceIndex] === token,
		),
	);
}

function containsTokensInOrder(
	tokens: readonly string[],
	requiredTokens: readonly string[],
): boolean {
	let requiredIndex = 0;

	for (const token of tokens) {
		if (token === requiredTokens[requiredIndex]) {
			requiredIndex += 1;
		}

		if (requiredIndex === requiredTokens.length) {
			return true;
		}
	}

	return false;
}

function phraseScore(
	queryTokens: readonly string[],
	searchableEntry: SearchableSitemapEntry,
): number {
	if (sequencesEqual(queryTokens, searchableEntry.titleTokens)) {
		return 650;
	}

	if (
		searchableEntry.searchTermTokens.some((tokens) =>
			sequencesEqual(queryTokens, tokens),
		)
	) {
		return 625;
	}

	if (containsSequence(searchableEntry.titleTokens, queryTokens)) {
		return 550;
	}

	if (
		searchableEntry.searchTermTokens.some((tokens) =>
			containsSequence(tokens, queryTokens),
		)
	) {
		return 525;
	}

	if (containsSequence(searchableEntry.guidanceTokens, queryTokens)) {
		return 500;
	}

	if (
		queryTokens.length > 1 &&
		searchableEntry.searchTermTokens.some(
			(tokens) =>
				tokens.length > 1 && containsTokensInOrder(queryTokens, tokens),
		)
	) {
		return 450;
	}

	if (
		queryTokens.length > 1 &&
		searchableEntry.titleTokens.length > 1 &&
		containsTokensInOrder(queryTokens, searchableEntry.titleTokens)
	) {
		return 425;
	}

	return 0;
}

function tokenSimilarity(
	queryToken: string,
	candidateToken: string,
	allowPartialTokenPrefix = false,
): number {
	if (queryToken === candidateToken) {
		return 1;
	}

	if (queryToken.length === 1 && candidateToken.includes(queryToken)) {
		return candidateToken.startsWith(queryToken) ? 0.9 : 0.7;
	}

	if (
		((allowPartialTokenPrefix && queryToken.length >= 1) ||
			(queryToken.length >= 2 && queryToken.length <= 3)) &&
		candidateToken.startsWith(queryToken)
	) {
		return 0.9;
	}

	if (
		queryToken.length < 4 ||
		candidateToken.length < 4 ||
		Math.abs(queryToken.length - candidateToken.length) > 2
	) {
		return 0;
	}

	const fuzzyMatch = fuzzysort.single(queryToken, candidateToken);

	return fuzzyMatch?.score && fuzzyMatch.score >= 0.2
		? fuzzyMatch.score * 0.8
		: 0;
}

function rankEntry(
	searchableEntry: SearchableSitemapEntry,
	queryTokens: readonly string[],
): RankedSitemapEntry | null {
	let matchedTokens = 0;
	let tokenScore = 0;

	for (const [queryIndex, queryToken] of queryTokens.entries()) {
		let bestTokenScore = 0;
		const allowPartialTokenPrefix =
			queryTokens.length > 1 && queryIndex === queryTokens.length - 1;

		for (const [candidateToken, weight] of searchableEntry.tokenWeights) {
			bestTokenScore = Math.max(
				bestTokenScore,
				tokenSimilarity(queryToken, candidateToken, allowPartialTokenPrefix) *
					weight,
			);
		}

		if (bestTokenScore > 0) {
			matchedTokens += 1;
			tokenScore += bestTokenScore;
		}
	}

	if (matchedTokens < Math.ceil(queryTokens.length * 0.6)) {
		return null;
	}

	const matchedTitleTokens = searchableEntry.titleTokens.filter((titleToken) =>
		queryTokens.some(
			(queryToken, queryIndex) =>
				tokenSimilarity(
					queryToken,
					titleToken,
					queryTokens.length > 1 && queryIndex === queryTokens.length - 1,
				) > 0,
		),
	).length;

	return {
		entry: searchableEntry.entry,
		score:
			phraseScore(queryTokens, searchableEntry) +
			(matchedTokens / queryTokens.length) * 100 +
			tokenScore * 10 +
			(matchedTitleTokens / searchableEntry.titleTokens.length) * 25,
	};
}

@Injectable({
	providedIn: 'root',
})
export class SitemapSearchService {
	public search(
		entries: readonly SitemapEntry[],
		criteria: SitemapSearchCriteria,
	): SitemapEntry[] {
		if (criteria.audiences.length === 0) {
			return [];
		}

		const audienceEntries = entries.filter((entry) =>
			entry.audiences.some((audience) => criteria.audiences.includes(audience)),
		);
		const query = criteria.query.trim();

		if (query === '') {
			return audienceEntries.sort((first, second) =>
				first.title.localeCompare(second.title),
			);
		}

		const queryTokens = normalizeTokens(query, query.length > 1);

		if (queryTokens.length === 0) {
			return [];
		}

		return audienceEntries
			.map(buildSearchableEntry)
			.map((entry) => rankEntry(entry, queryTokens))
			.filter((entry): entry is RankedSitemapEntry => entry !== null)
			.sort((first, second) => {
				const scoreDifference = second.score - first.score;

				if (scoreDifference !== 0) {
					return scoreDifference;
				}

				const currentApplicationDifference =
					Number(second.entry.application === criteria.currentApplication) -
					Number(first.entry.application === criteria.currentApplication);

				if (currentApplicationDifference !== 0) {
					return currentApplicationDifference;
				}

				return first.entry.title.localeCompare(second.entry.title);
			})
			.map((result) => result.entry);
	}
}
